import React, { useMemo, useCallback, useState, useEffect } from "react";
import { GameBoard } from "./components/game-board.tsx";
import { TargetPreview } from "./components/target-preview.tsx";
import { FoldControls } from "./components/fold-controls.tsx";
import { useGame } from "./hooks/use-game.ts";
import { getDailyPuzzle, fetchDailyPuzzle, getPuzzleNumber } from "./lib/daily.ts";
import { loadStats, saveStats, recordCompletion } from "./lib/storage.ts";
import type { GameState, Puzzle } from "./types.ts";

export function App({ onWin, embedded, winExtra, difficulty }: { onWin?: (data: { stars: number; metric: number; timeMs: number }) => void; embedded?: boolean; winExtra?: React.ReactNode; difficulty?: string } = {}) {
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const puzzleNumber = useMemo(() => getPuzzleNumber(), []);

  useEffect(() => {
    setPuzzle(null);
    fetchDailyPuzzle(difficulty)
      .then(setPuzzle)
      .catch(() => setPuzzle(getDailyPuzzle(difficulty)));
  }, [difficulty]);

  if (!puzzle) {
    return (
      <div className={`flex flex-col items-center justify-center ${embedded ? 'p-4 min-h-[300px]' : 'min-h-screen p-6'} max-w-[700px] mx-auto select-none`}>
        <div className="animate-pulse text-slate-500 text-sm font-mono">Loading puzzle...</div>
      </div>
    );
  }

  return <GameInner puzzle={puzzle} puzzleNumber={puzzleNumber} onWin={onWin} embedded={embedded} winExtra={winExtra} />;
}

function GameInner({ puzzle, puzzleNumber, onWin, embedded, winExtra }: { puzzle: Puzzle; puzzleNumber: number; onWin?: (data: { stars: number; metric: number; timeMs: number }) => void; embedded?: boolean; winExtra?: React.ReactNode }) {
  const { state, doFold, undo, reset } = useGame(puzzle);
  const [showHelp, setShowHelp] = useState(false);
  const [stats, setStats] = useState(() => loadStats());

  const stars = state.stars;
  const won = state.phase === "won";

  // Save completion to localStorage
  useEffect(() => {
    if (won && !stats.history[puzzle.id]?.completed) {
      const timeMs = (state.completedAt ?? Date.now()) - state.startedAt;
      const updated = recordCompletion(stats, {
        puzzleId: puzzle.id,
        completed: true,
        stars: state.stars,
        folds: state.foldHistory,
        timeMs,
      });
      setStats(updated);
      saveStats(updated);
      onWin?.({ stars: state.stars, metric: state.foldHistory.length, timeMs });
    }
  }, [won]);

  return (
    <div className={`flex flex-col items-center ${embedded ? 'p-4 gap-4' : 'min-h-screen p-6 gap-6'} max-w-[700px] mx-auto select-none`}>
      {/* Header — hidden when embedded in hub */}
      {!embedded && (
        <div className="flex items-center justify-between w-full">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Foldaway</h1>
            <div className="flex gap-3 text-sm text-slate-500">
              <span>#{puzzleNumber}</span>
              {stats.streak > 0 && (
                <span className="text-amber-400">{stats.streak} day streak</span>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-800 text-slate-400 hover:text-white text-sm font-bold border border-slate-700"
          >
            ?
          </button>
        </div>
      )}

      {/* Subheader when embedded */}
      {embedded && (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-slate-500">#{puzzleNumber}</span>
            {stats.streak > 0 && (
              <span className="font-mono text-xs text-violet-400">{stats.streak} day streak</span>
            )}
          </div>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-[#1A1D33] text-slate-400 hover:text-white text-xs font-bold border border-[#2A2D42]"
          >
            ?
          </button>
        </div>
      )}

      {/* Help */}
      {showHelp && (
        <div className="w-full bg-slate-800/80 rounded-xl p-4 text-sm text-slate-300 border border-slate-700 space-y-2">
          <p className="font-medium text-white">How to play</p>
          <p>
            Fold the paper so the visible top matches the{" "}
            <strong>Target</strong> pattern.
          </p>
          <ol className="list-decimal list-inside space-y-1">
            <li>
              Click a{" "}
              <span className="text-amber-400">fold line</span> (dashed line
              between cells)
            </li>
            <li>Choose the fold direction (which side folds over)</li>
            <li>Match the target in as few folds as possible</li>
          </ol>
          <p className="text-slate-500">
            Empty cells are transparent — you can see through them to layers
            below.
          </p>
        </div>
      )}

      {/* Game area */}
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 w-full">
        <div className="flex-1 w-full">
          <GameBoard grid={state.currentGrid} onFold={doFold} disabled={won} />
        </div>
        <div className="flex flex-col items-center gap-4">
          <TargetPreview target={puzzle.target} />
          <div className="text-xs text-slate-500">
            {state.currentGrid.rows}x{state.currentGrid.cols}
            {" → "}
            {puzzle.target.length}x{puzzle.target[0]?.length ?? 0}
          </div>
        </div>
      </div>

      {/* Controls */}
      <FoldControls
        foldCount={state.foldHistory.length}
        solutionLength={puzzle.solution.length}
        canUndo={state.foldHistory.length > 0 && !won}
        onUndo={undo}
        onReset={reset}
      />

      {/* Win state */}
      {won && (
        <div className="w-full bg-emerald-900/40 border border-emerald-700 rounded-xl p-6 text-center space-y-3 animate-[fadeIn_0.3s_ease-out]">
          <div className="text-4xl">
            {"★".repeat(stars)}
            {"☆".repeat(3 - stars)}
          </div>
          <p className="text-xl font-bold text-emerald-300">Solved!</p>
          <p className="text-sm text-slate-400">
            {state.foldHistory.length} fold
            {state.foldHistory.length !== 1 ? "s" : ""}
            {" — "}
            {stars === 3
              ? "Perfect!"
              : stars === 2
                ? "Great job!"
                : "You got it!"}
          </p>
          <div className="flex gap-3 justify-center pt-2">
            <ShareButton state={state} puzzleNumber={puzzleNumber} />
          </div>
          {stats.totalPlayed > 0 && !embedded && (
            <div className="pt-3 border-t border-emerald-800 text-xs text-slate-500 flex justify-center gap-6">
              <span>
                Played: <strong className="text-slate-300">{stats.totalPlayed}</strong>
              </span>
              <span>
                Total stars: <strong className="text-amber-400">{stats.totalStars}</strong>
              </span>
              <span>
                Best streak: <strong className="text-slate-300">{stats.maxStreak}</strong>
              </span>
            </div>
          )}
          {winExtra}
        </div>
      )}
    </div>
  );
}

function ShareButton({
  state,
  puzzleNumber,
}: {
  state: GameState;
  puzzleNumber: number;
}) {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(() => {
    const stars = state.stars;
    const foldEmojis = state.foldHistory
      .map((f) => {
        if (f.direction === "up") return "\u2B06\uFE0F";
        if (f.direction === "down") return "\u2B07\uFE0F";
        if (f.direction === "left") return "\u2B05\uFE0F";
        return "\u27A1\uFE0F";
      })
      .join("");

    const text = [
      `\uD83D\uDCC4 Foldaway #${puzzleNumber}`,
      `${"★".repeat(stars)}${"☆".repeat(3 - stars)} (${state.foldHistory.length} fold${state.foldHistory.length !== 1 ? "s" : ""})`,
      foldEmojis,
    ].join("\n");

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [state, puzzleNumber]);

  return (
    <button
      onClick={handleShare}
      className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-medium transition-colors"
    >
      {copied ? "Copied!" : "Share Result"}
    </button>
  );
}
